import { getPerson } from '../../data/company';
import { addDays, startOfToday, toIsoDate } from '../../data/dates';
import type { PersonId } from '../../data/types';
import type {
  ActivityActor,
  ApprovalStep,
  Approver,
  CheckStatus,
  ProcurementRequest,
  RequestFilter,
  RequestStage,
} from './requests-types';

export const STAGE_LABEL: Record<RequestStage, string> = {
  intake: 'Intake',
  review: 'Review',
  sourcing: 'Sourcing',
  contracting: 'Contracting',
  ordering: 'Ordering',
  done: 'Done',
};

const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/** Epoch ms `n` hours before now. */
export function hoursAgoMs(n: number): number {
  return Date.now() - n * MS_PER_HOUR;
}

/** Epoch ms `n` days before now. */
export function daysAgoMs(n: number): number {
  return Date.now() - n * MS_PER_DAY;
}

/** ISO date `n` days from today. */
export function isoDaysFromNow(n: number): string {
  return toIsoDate(addDays(startOfToday(), n));
}

/** ISO date of the next time `month` (1-12) / `day` occurs, today included. */
export function nextOccurrenceIso(
  month: number,
  day: number,
  from: Date = startOfToday(),
): string {
  const thisYear = new Date(from.getFullYear(), month - 1, day);
  const target =
    thisYear.getTime() >= from.getTime()
      ? thisYear
      : new Date(from.getFullYear() + 1, month - 1, day);
  return toIsoDate(target);
}

export function approverName(approver: Approver): string {
  return approver.kind === 'person'
    ? getPerson(approver.id).name
    : approver.name;
}

export function approverTitle(approver: Approver): string {
  return approver.kind === 'person'
    ? getPerson(approver.id).title
    : approver.title;
}

/** Two-letter initials from the first two alphabetic words of a name. */
export function nameInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w))
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

export function approverInitials(approver: Approver): string {
  if (approver.kind === 'person') return getPerson(approver.id).initials;
  return nameInitials(approver.name);
}

export function actorName(actor: ActivityActor): string {
  return actor.kind === 'agent' ? 'Gently agent' : approverName(actor);
}

export function isPersonApprover(
  approver: Approver,
  personId: PersonId,
): boolean {
  return approver.kind === 'person' && approver.id === personId;
}

/** The first approval step that is not approved yet, or null when all are. */
export function currentApprovalStep(
  req: ProcurementRequest,
): ApprovalStep | null {
  return req.approvals.find((s) => s.status !== 'approved') ?? null;
}

export function approvalCounts(req: ProcurementRequest): {
  done: number;
  total: number;
} {
  return {
    done: req.approvals.filter((s) => s.status === 'approved').length,
    total: req.approvals.length,
  };
}

export function hasChangesRequested(req: ProcurementRequest): boolean {
  return req.approvals.some((s) => s.status === 'changes_requested');
}

/** Approval actions are available once intake is complete and before done. */
export function isRouted(req: ProcurementRequest): boolean {
  return req.stage !== 'intake' && req.stage !== 'done';
}

export function needsApprovalFrom(
  req: ProcurementRequest,
  personId: PersonId,
): boolean {
  if (!isRouted(req)) return false;
  const step = currentApprovalStep(req);
  return (
    step !== null &&
    step.status === 'open' &&
    isPersonApprover(step.approver, personId)
  );
}

export function matchesFilter(
  req: ProcurementRequest,
  filter: RequestFilter,
  personId: PersonId,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'mine':
      return req.requesterId === personId;
    case 'needs-approval':
      return needsApprovalFrom(req, personId);
    case 'direct':
      return req.type === 'direct';
    case 'indirect':
      return req.type === 'indirect';
  }
}

export const REQUEST_FILTERS: RequestFilter[] = [
  'all',
  'mine',
  'needs-approval',
  'direct',
  'indirect',
];

export function countByFilter(
  requests: ProcurementRequest[],
  personId: PersonId,
): Record<RequestFilter, number> {
  const counts: Record<RequestFilter, number> = {
    all: 0,
    mine: 0,
    'needs-approval': 0,
    direct: 0,
    indirect: 0,
  };
  for (const req of requests) {
    for (const filter of REQUEST_FILTERS) {
      if (matchesFilter(req, filter, personId)) counts[filter] += 1;
    }
  }
  return counts;
}

export interface CheckCounts {
  pass: number;
  flag: number;
  fail: number;
  pending: number;
}

export function checkCounts(req: ProcurementRequest): CheckCounts {
  const counts: CheckCounts = { pass: 0, flag: 0, fail: 0, pending: 0 };
  for (const check of req.checks) {
    const status: CheckStatus = check.status;
    if (status === 'pass') counts.pass += 1;
    else if (status === 'flag') counts.flag += 1;
    else if (status === 'fail') counts.fail += 1;
    else if (status === 'pending') counts.pending += 1;
  }
  return counts;
}

/** '6h', '3d', 'now'. */
export function formatAge(createdAt: number, now: number = Date.now()): string {
  const diff = Math.max(0, now - createdAt);
  if (diff < 5 * 60_000) return 'now';
  if (diff < MS_PER_DAY)
    return `${Math.max(1, Math.floor(diff / MS_PER_HOUR))}h`;
  return `${Math.floor(diff / MS_PER_DAY)}d`;
}

/** 'just now', '12 min ago', '5 hours ago', '3 days ago'. */
export function formatActivityTime(
  at: number,
  now: number = Date.now(),
): string {
  const diff = Math.max(0, now - at);
  if (diff < 60_000) return 'just now';
  if (diff < MS_PER_HOUR) {
    return `${Math.floor(diff / 60_000)} min ago`;
  }
  if (diff < MS_PER_DAY) {
    const hours = Math.floor(diff / MS_PER_HOUR);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  const days = Math.floor(diff / MS_PER_DAY);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}
